import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FrameRecords } from "../types/api";
import type { VisibleColumn } from "../lib/columns";
import { makeVisibleMatcher } from "../lib/textSearch";
import {
  isMultiTerm, parseTerms, rowMatchesAnyTerm, scoreColumnsCached, unmatchedTerms,
} from "../lib/multiSearch";

// THE GRID SEARCH, shared by every table (finance 2026-09-28; built for the Manual Match
// grids). One value behaves as a plain search box; a column pasted out of Excel searches
// for every value at once, scoped to one column or the whole cell on request, and reports
// the values that found nothing.

export type MultiSearch = ReturnType<typeof useMultiSearch>;

/** Search state over `frame`, searching the rendered columns `vis`.
 *
 *  `missingForOneValue`: also work out whether a single typed value is missing. Only the
 *  found-in-matched panel needs that; anywhere else it is a scan per keystroke that nothing
 *  shows. A paste always reports its missing values (the read-back). */
export function useMultiSearch(frame: FrameRecords, vis: VisibleColumn[],
                               { missingForOneValue = false } = {}) {
  const [text, setText] = useState("");
  // The column key the user narrowed to, or "" for every rendered column; `exact` switches
  // per-term substring matching to whole-cell.
  const [searchCol, setSearchCol] = useState("");
  const [exact, setExact] = useState(false);

  // Pasted values, split on Excel's own delimiters (tab between cells, newline between
  // rows). Two or more switch on the multi-value affordances.
  const terms = useMemo(() => parseTerms(text), [text]);
  const multi = isMultiTerm(terms);

  // The columns the search actually runs over: every rendered column, or just the one the
  // user narrowed to. A narrowed column that is no longer rendered (a pin change, a new
  // frame) falls back to all columns rather than searching nothing.
  const searchCols = useMemo(() => {
    if (!searchCol) return vis;
    const hit = vis.find((c) => c.key === searchCol);
    return hit ? [hit] : vis;
  }, [vis, searchCol]);

  // Which columns actually contain the pasted values, best-coverage first. Only columns
  // matching at least one value are listed — the picker's job is to point at the column
  // the references live in, not to re-list every column in the frame.
  const coverage = useMemo(() => {
    if (!multi) return [];
    return scoreColumnsCached(frame, frame.rows, vis, terms, exact);
  }, [multi, frame, vis, terms, exact]);

  // Values that matched no row at all, within the columns being searched. Reported so a
  // paste of 24 refs returning 22 rows can never read as a complete result (§6).
  const missing = useMemo(() => {
    if (!terms.length || (!isMultiTerm(terms) && !missingForOneValue)) return [];
    return unmatchedTerms(frame.rows, searchCols, terms, exact);
  }, [frame.rows, searchCols, terms, exact, missingForOneValue]);

  // How many values exist ANYWHERE in the grid — the "All columns" option's true score.
  // Derived from the same scan so the picker and the badge can never disagree.
  const allColumnsHits = useMemo(() => {
    if (!multi) return 0;
    if (!searchCol) return terms.length - missing.length;
    return terms.length - unmatchedTerms(frame.rows, vis, terms, exact).length;
  }, [multi, searchCol, terms, missing, frame.rows, vis, exact]);

  // A narrowed column only makes sense while it still carries some pasted value. When the
  // paste changes (or is cleared) to something that column has nothing of, drop back to
  // all columns rather than silently showing an empty grid.
  useEffect(() => {
    if (!searchCol) return;
    if (!multi) { setSearchCol(""); return; }
    if (!coverage.some((c) => c.key === searchCol)) setSearchCol("");
  }, [multi, coverage, searchCol]);

  // Does the row at position `p` of `frame` pass the search? null while nothing is typed.
  // Built once per change, never per row — see makeVisibleMatcher.
  const rowTest = useMemo<((p: number, r: FrameRecords["rows"][number]) => boolean) | null>(() => {
    // UNION over the pasted values: the user pasted a column of references and wants the
    // rows for all of them.
    if (multi) return (_p, r) => rowMatchesAnyTerm(r, searchCols, terms, exact);
    // The parsed term, never the raw box: a paste repeating one value ("ABC\r\nABC", a
    // quoted Excel cell) is ONE term but still carries its separators and quotes.
    const t = terms.length ? terms[0].needle : "";
    if (!t) return null;
    // Matches the DISPLAYED text (a date reads dd/mm/yyyy on screen, yyyy-mm-dd in the
    // payload; an amount 2,244.90 or 2244.90), so what is on screen is findable.
    const match = makeVisibleMatcher(frame, searchCols, t);
    return (p) => match(p);
  }, [multi, frame, searchCols, terms, exact]);

  const reset = useCallback(() => { setText(""); setSearchCol(""); setExact(false); }, []);

  return {
    text, setText, searchCol, setSearchCol, exact, setExact, terms, multi, searchCols,
    coverage, missing, allColumnsHits, rowTest, reset,
  };
}

// THE SEARCH PANEL. One surface holding the box, the scope controls and the read-back,
// styled from the design tokens (.msearch in theme.css). It lifts only while a multi-value
// query is live: a quiet control at rest, a workspace when it is doing something. `tools`
// trail the box on its row; `children` sit under the read-back (the Manual Match grids'
// bulk tick and found-in-matched panel).
export function SearchPanel({ search, matchingRows, heading, compact = false, tools, children }: {
  search: MultiSearch;
  matchingRows: number;
  heading?: React.ReactNode;
  compact?: boolean;
  tools?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const { text, setText, searchCol, setSearchCol, exact, setExact, terms, multi, coverage,
          missing, allColumnsHits } = search;

  // AUTO-GROWING SEARCH BOX. It must read as an ordinary one-line search field when
  // empty — that is what it is, most of the time — and grow to fit a pasted column
  // without the user resizing anything. A fixed `rows` cannot do both: 1 row clips a
  // paste, 3 rows leaves a permanently oversized box on a tab where two of these sit
  // side by side.
  //
  // Measured, not guessed: reset the height, read scrollHeight (the content's natural
  // height), and adopt it, capped so a 200-value paste scrolls internally instead of
  // pushing the grid off screen. Runs on every text change, so typing, pasting and
  // clearing all settle to the right size with no jump.
  const boxRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    el.style.height = "auto";
    // scrollHeight is the CONTENT-box height (padding included, border NOT). The element
    // is border-box, so its `height` must also carry the border — without this the box
    // renders 2px short of its own text and clips the first line, which is exactly the
    // bug finance reported.
    const cs = getComputedStyle(el);
    const border = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const needed = el.scrollHeight + border;
    const cap = 150;  // ~6 lines; beyond that the box scrolls rather than grows
    el.style.height = Math.min(needed, cap) + "px";
    el.style.overflowY = needed > cap ? "auto" : "hidden";
  }, [text]);

  return (
    <div className={"msearch" + (multi ? " msearch--active" : "") + (compact ? " msearch--compact" : "")}>
      <div className="msearch-row">
        {compact && heading && <span className="msearch-heading">{heading}</span>}
        {/* A textarea, not an input: pasting a column out of Excel carries newlines and
            an <input> silently flattens them to spaces — which is what made a multi-row
            paste unsearchable in the first place. Height is MEASURED (see the auto-size
            effect above), never left to `rows`: .input adds 8px padding and width:100%
            with no box-sizing, so rows={1} rendered shorter than its own line-height and
            clipped the placeholder. */}
        <textarea
          ref={boxRef}
          className="input msearch-box"
          rows={1}
          placeholder="Search or paste a column…"
          title="Search across all columns. Paste a column of values straight from Excel — from your own worksheet — to search for all of them at once."
          value={text}
          onChange={(e) => setText(e.target.value)}
          // After a paste the caret sits at the end, so the box scrolls to its LAST
          // line and hides what was just pasted. Return to the top — the first values
          // are the ones the user checks against their worksheet.
          onPaste={(e) => {
            const el = e.currentTarget;
            window.setTimeout(() => { el.scrollTop = 0; }, 0);
          }}
        />
        {multi && (
          <>
            <span className="msearch-scope">
              Search in
              <select value={searchCol} onChange={(e) => setSearchCol(e.target.value)}>
                {/* Counted from the union of every column, NOT assumed to be all of
                    them: a mistyped value exists in no column, and claiming "24 of 24"
                    while the chip below reports one missing would contradict itself. */}
                <option value="">All columns · {allColumnsHits}/{terms.length}</option>
                {coverage.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label} · {c.hits}/{terms.length}
                  </option>
                ))}
              </select>
            </span>
            <label
              className={"msearch-toggle" + (exact ? " msearch-toggle--on" : "")}
              title="Match the whole cell rather than any part of it — use when a short value like 55 pulls in rows it shouldn't."
            >
              <input type="checkbox" checked={exact}
                onChange={(e) => setExact(e.target.checked)} />
              Whole cell
            </label>
          </>
        )}
        {tools}
      </div>

      {/* The read-back. Never let a paste of N values return fewer than N without
          saying which ones found nothing — a silently short result reads as a complete
          one, and that is how a genuine unmatched item gets missed. */}
      {multi && (
        <div className="msearch-readback">
          <span className="msearch-count">
            <strong>{terms.length}</strong> pasted ·{" "}
            <strong>{matchingRows.toLocaleString()}</strong> matching row
            {matchingRows === 1 ? "" : "s"}
            {searchCol ? "" : " · all columns"}
          </span>
          {missing.length === 0 ? (
            <span className="msearch-chip msearch-chip--ok">✓ all {terms.length} found</span>
          ) : (
            <span className="msearch-chip msearch-chip--warn">
              {missing.length} not found
              <code>{missing.slice(0, 4).map((t) => t.raw).join(", ")}
                {missing.length > 4 ? ` +${missing.length - 4}` : ""}</code>
            </span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
